import { useState } from 'react';

import { RevenueTrend } from '../components/Charts.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { money, moneyShort, number, seriesColor } from '../lib/format.ts';
import { ErrorState } from './Upgrade.tsx';

type Granularity = 'day' | 'week';

export function PerformancePage() {
  const [days, setDays] = useState(30);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const performance = useAsync(() => api.insights.performance(days, granularity), [days, granularity]);
  const recommendations = useAsync(() => api.insights.recommendations().catch(() => null), []);

  if (performance.loading) return <Spinner />;
  if (performance.error) return <ErrorState error={performance.error} />;

  const { shops, portfolioAvailable, totalShops } = performance.data!;

  // One line per shop, capped at the eight fixed categorical slots. A ninth
  // shop folds into "Other" rather than reusing a hue already on screen.
  const visible = shops.slice(0, 8);
  const buckets = new Set<string>();
  for (const shop of shops) for (const point of shop.series) buckets.add(point.bucket);

  const chartData = [...buckets]
    .sort()
    .map((bucket) => {
      const row: Record<string, number | string> = { bucket };
      visible.forEach((shop, index) => {
        row[`s${index}`] = shop.series.find((p) => p.bucket === bucket)?.revenue ?? 0;
      });
      if (shops.length > 8) {
        row.other = shops
          .slice(8)
          .reduce((acc, shop) => acc + (shop.series.find((p) => p.bucket === bucket)?.revenue ?? 0), 0);
      }
      return row;
    });

  const series = visible.map((shop, index) => ({
    key: `s${index}`,
    label: shop.shopName,
    color: seriesColor(index),
  }));
  if (shops.length > 8) {
    series.push({ key: 'other', label: `Other (${shops.length - 8} shops)`, color: 'var(--axis)' });
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Shop performance</h1>
          <p>Realized revenue, units and average price per shop over time.</p>
        </div>
        <div className="toolbar">
          <div className="pill-row">
            {[7, 30, 90].map((option) => (
              <button
                key={option}
                className={`pill ${days === option ? 'active' : ''}`}
                onClick={() => setDays(option)}
              >
                {option}d
              </button>
            ))}
          </div>
          <div className="pill-row">
            {(['day', 'week'] as Granularity[]).map((option) => (
              <button
                key={option}
                className={`pill ${granularity === option ? 'active' : ''}`}
                onClick={() => setGranularity(option)}
              >
                by {option}
              </button>
            ))}
          </div>
        </div>
      </header>

      {!portfolioAvailable && totalShops > 1 && (
        <div style={{ marginBottom: 16 }}>
          <Notice>
            <strong>Showing your busiest shop only.</strong> You have {totalShops} shops connected -
            Pro adds the full firm portfolio view.
          </Notice>
        </div>
      )}

      <Card title="Revenue over time" subtitle="One line per shop">
        {chartData.length === 0 ? (
          <Empty title="No sales in this window" />
        ) : (
          <RevenueTrend data={chartData} series={series} height={300} />
        )}
      </Card>

      <Card title="Shop totals">
        <DataTable
          rows={shops}
          rowKey={(shop) => shop.shopId ?? shop.shopName}
          initialSort={{ key: 'revenue', dir: 'desc' }}
          searchPlaceholder="Search shops"
          columns={[
            {
              key: 'shop',
              header: 'Shop',
              searchText: (shop) => shop.shopName,
              sortValue: (shop) => shop.shopName,
              render: (shop) => {
                const index = shops.indexOf(shop);
                return (
                  <>
                    <span
                      className="legend-swatch line"
                      style={{
                        background: index < 8 ? seriesColor(index) : 'var(--axis)',
                        display: 'inline-block',
                        marginRight: 8,
                      }}
                    />
                    {shop.shopName}
                  </>
                );
              },
            },
            {
              key: 'revenue',
              header: 'Revenue',
              align: 'num',
              sortValue: (shop) => shop.revenue,
              render: (shop) => moneyShort(shop.revenue),
            },
            {
              key: 'units',
              header: 'Units',
              align: 'num',
              sortValue: (shop) => shop.units,
              render: (shop) => number(shop.units),
            },
            {
              key: 'transactions',
              header: 'Transactions',
              align: 'num',
              sortValue: (shop) => shop.transactions,
              render: (shop) => number(shop.transactions),
            },
            {
              key: 'avgPrice',
              header: 'Avg price',
              align: 'num',
              sortValue: (shop) => shop.averagePrice,
              render: (shop) => money(shop.averagePrice),
            },
            {
              key: 'items',
              header: 'Distinct items',
              align: 'num',
              sortValue: (shop) => shop.distinctItems,
              render: (shop) => number(shop.distinctItems),
            },
          ]}
        />
      </Card>

      {recommendations.data && recommendations.data.recommendations.length > 0 && (
        <Card
          title="Pricing recommendations"
          subtitle={recommendations.data.note}
        >
          <DataTable
            rows={recommendations.data.recommendations}
            rowKey={(rec) => rec.itemCode}
            initialSort={{ key: 'delta', dir: 'desc' }}
            searchPlaceholder="Search items"
            pageSize={20}
            columns={[
              {
                key: 'item',
                header: 'Item',
                searchText: (rec) => `${rec.displayName} ${rec.itemCode}`,
                sortValue: (rec) => rec.displayName,
                render: (rec) => <strong>{rec.displayName}</strong>,
              },
              {
                key: 'now',
                header: 'Now',
                align: 'num',
                sortValue: (rec) => rec.currentPrice,
                render: (rec) => money(rec.currentPrice),
              },
              {
                key: 'suggested',
                header: 'Suggested',
                align: 'num',
                sortValue: (rec) => rec.recommendedPrice,
                render: (rec) => <strong>{money(rec.recommendedPrice)}</strong>,
              },
              {
                key: 'projected',
                header: 'Projected units/day',
                align: 'num',
                sortValue: (rec) => rec.projectedUnitsPerDay,
                render: (rec) => rec.projectedUnitsPerDay.toFixed(2),
              },
              {
                key: 'delta',
                header: 'Revenue change',
                align: 'num',
                sortValue: (rec) => rec.deltaRevenuePct,
                render: (rec) => (
                  <span className={rec.deltaRevenuePct >= 0 ? 'delta-up' : 'delta-down'}>
                    {rec.deltaRevenuePct > 0 ? '+' : ''}
                    {rec.deltaRevenuePct.toFixed(1)}%
                  </span>
                ),
              },
              {
                key: 'rationale',
                header: 'Rationale',
                searchText: (rec) => rec.rationale,
                render: (rec) => (
                  <span className="small muted" style={{ display: 'block', maxWidth: 380 }}>
                    {rec.rationale}
                  </span>
                ),
              },
            ]}
          />
        </Card>
      )}
    </>
  );
}
